/**
 * features/customers/CustomerFormDialog.tsx
 */

import { useEffect, useState } from 'react';
import { customerInputSchema, type CustomerInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import type { Party } from '~/lib/types';
import { Dialog } from '~/ui/overlays';
import { Button, Field, Input, Textarea } from '~/ui/primitives';
import { useToast } from '~/ui/overlays';
import { TaxIdLookupField } from '~/features/tax-lookup/TaxIdLookupField';
import { useCreateCustomer, useUpdateCustomer } from './hooks';

type FormState = CustomerInput;

const EMPTY: FormState = {
  name: '',
  taxId: '',
  branch: '',
  address: '',
  phone: '',
  email: '',
  notes: '',
};

export function CustomerFormDialog({
  open,
  onClose,
  customer,
}: {
  open: boolean;
  onClose: () => void;
  customer: Party | null;
}) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      customer
        ? {
            name: customer.name,
            taxId: customer.taxId,
            branch: customer.branch,
            address: customer.address,
            phone: customer.phone,
            email: customer.email,
            notes: customer.notes,
          }
        : EMPTY,
    );
  }, [open, customer]);

  const saving = create.isPending || update.isPending;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit() {
    const parsed = customerInputSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    try {
      if (customer) {
        await update.mutateAsync({ id: customer.id, input: parsed.data });
        toast.success('บันทึกข้อมูลลูกค้าแล้ว');
      } else {
        await create.mutateAsync(parsed.data);
        toast.success('เพิ่มลูกค้าใหม่แล้ว');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={customer ? 'แก้ไขข้อมูลลูกค้า' : 'เพิ่มลูกค้าใหม่'}
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} loading={saving}>
            บันทึก
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="ชื่อลูกค้า" required error={errors.name}>
          <Input
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>

        <TaxIdLookupField
          value={form.taxId}
          onChange={(value) => setField('taxId', value)}
          error={errors.taxId}
          onFound={(branch) => {
            setForm((current) => ({
              ...current,
              taxId: branch.taxId,
              branch: branch.branch,
              name: current.name || branch.name,
              address: branch.address || current.address,
              phone: branch.phone || current.phone,
            }));
          }}
        />

        <Field label="ที่อยู่" error={errors.address}>
          <Textarea
            value={form.address}
            onChange={(event) => setField('address', event.target.value)}
            invalid={Boolean(errors.address)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="โทรศัพท์" error={errors.phone}>
            <Input value={form.phone} onChange={(event) => setField('phone', event.target.value)} />
          </Field>
          <Field label="อีเมล" error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(event) => setField('email', event.target.value)}
            />
          </Field>
        </div>

        <Field label="หมายเหตุ" error={errors.notes}>
          <Textarea value={form.notes} onChange={(event) => setField('notes', event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
